/**
 * Associazione strumento → ticker Trading 212 (SPEC-014, rischio R1).
 *
 * Vive nel modulo connettori e non nella wishlist perché è un'anagrafica del
 * broker: la wishlist è solo il primo posto in cui serve. Un secondo punto di
 * ingresso (dettaglio strumento) potrà riusarlo senza spostarlo.
 *
 * ## Le tre regole che questo componente esiste per rispettare
 *
 * 1. **Sceglie l'utente, sempre.** La corrispondenza per ISIN viene messa in
 *    cima e marcata, ma non viene mai preselezionata né associata da sola.
 *    Associare automaticamente il primo risultato plausibile è l'errore che
 *    F-048 ha già pagato una volta, con un ticker salvato senza conferma.
 * 2. **Si cerca a comando, non a ogni battitura.** Il catalogo del broker è
 *    limitato a una richiesta ogni 50 secondi e arriva completo: la prima
 *    ricerca può richiedere secondi, le successive sono immediate perché il
 *    backend tiene il catalogo in memoria. Un campo con ricerca automatica
 *    darebbe l'illusione contraria.
 * 3. **Dice cosa succede senza mappatura**, invece di limitarsi a essere vuoto:
 *    è la differenza fra "non ho compilato un campo facoltativo" e "il motore
 *    non potrà eseguire ordini su questo titolo".
 */

import { useMemo, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Button, Field, Select } from '@/ui';
import { useAssignTicker, useConnections, useProposeTicker, useTickerSearch, type TickerMatch } from './api';
import './broker-ticker.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

export type BrokerTickerFieldProps = {
  instrumentId: string;
  instrumentName: string;
  /** Mappatura già salvata, se esiste. */
  currentTicker: string | null;
  /** Testo con cui precompilare la ricerca: di norma il simbolo o il nome. */
  defaultQuery?: string;
};

export function BrokerTickerField({
  instrumentId,
  instrumentName,
  currentTicker,
  defaultQuery = '',
}: BrokerTickerFieldProps) {
  const connections = useConnections();
  const search = useTickerSearch();
  const assign = useAssignTicker();
  const propose = useProposeTicker();

  const [query, setQuery] = useState(defaultQuery);
  const [selected, setSelected] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState('');
  // Stato locale invece di rileggere la prop: il modal non si rimonta dopo il
  // salvataggio, e senza questo l'utente vedrebbe ancora il valore precedente.
  const [saved, setSaved] = useState<string | null>(currentTicker);

  const usable = useMemo(
    () => (connections.data?.items ?? []).filter((connection) => connection.status === 'active'),
    [connections.data],
  );

  const activeConnectionId = connectionId || usable[0]?.id || '';
  const results = search.data?.items ?? [];

  const runSearch = () => {
    if (query.trim().length < 2 || activeConnectionId === '') return;
    setSelected(null);
    search.mutate({ connectionId: activeConnectionId, query: query.trim(), instrumentId });
  };

  const confirm = (ticker: string | null) => {
    assign.mutate(
      { instrumentId, ticker },
      {
        onSuccess: (data) => {
          setSaved(data.trading212Ticker);
          setSelected(null);
          search.reset();
        },
      },
    );
  };

  return (
    <div className="broker-ticker">
      <div className="broker-ticker__head">
        <span className="broker-ticker__label">Ticker Trading 212</span>
        {saved ? (
          <Badge tone="positive">{saved}</Badge>
        ) : (
          <Badge tone="accent">non associato</Badge>
        )}
      </div>

      <p className="broker-ticker__hint">
        {saved
          ? `«${instrumentName}» è associato al ticker ${saved}: l'auto-trading può inviare ordini su questo titolo.`
          : `Senza associazione l'auto-trading non può inviare ordini su «${instrumentName}» tramite il broker (la registrazione su un portafoglio simulato funziona comunque).`}
      </p>

      {usable.length === 0 ? (
        <Alert variant="info" title="Nessuna connessione broker attiva">
          La ricerca usa il catalogo di Trading 212, quindi richiede una connessione attiva. Puoi salvare
          il candidato adesso e associare il ticker più avanti.
        </Alert>
      ) : (
        <>
          {usable.length > 1 && (
            <Select
              label="Connessione su cui cercare"
              value={activeConnectionId}
              onChange={(event) => setConnectionId(event.target.value)}
              options={usable.map((connection) => ({
                value: connection.id,
                label: `${connection.label} (${connection.environment})`,
              }))}
              hint="Demo e live possono esporre cataloghi diversi."
            />
          )}

          <div className="broker-ticker__search">
            <Field
              label="Cerca nel catalogo del broker"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              // Invio dentro un form annidato invierebbe il form della wishlist:
              // qui l'invio significa "cerca", e va fermato prima che risalga.
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  runSearch();
                }
              }}
              hint="Nome o ticker, almeno due caratteri. La prima ricerca scarica il catalogo e può richiedere qualche secondo."
            />
            <Button type="button" variant="secondary" onClick={runSearch} disabled={search.isPending || query.trim().length < 2}>
              {search.isPending ? 'Cerco…' : 'Cerca'}
            </Button>
            {/* La proposta non ha bisogno della query: parte dallo strumento,
                che il backend conosce già meglio di quanto l'utente possa
                digitare (ISIN, simbolo del provider, borsa). */}
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                propose.mutate({
                  instrumentId,
                  ...(activeConnectionId ? { connectionId: activeConnectionId } : {}),
                })
              }
              disabled={propose.isPending || activeConnectionId === ''}
            >
              {propose.isPending ? 'Identifico…' : 'Proponi tu'}
            </Button>
          </div>

          {propose.error && (
            <Alert variant="error" title="Proposta non riuscita">
              {errorMessage(propose.error, 'Errore imprevisto')}
            </Alert>
          )}

          {propose.data && (
            <Alert
              // ⚠️ Il tono segue `autoAssignable`, non la sola confidenza: una
              // confidenza alta su una riga il cui nome non c'entra è il caso
              // peggiore, e deve avere l'aria di un avviso, non di una conferma.
              variant={propose.data.autoAssignable ? 'info' : 'warning'}
              title={
                propose.data.chosenTicker
                  ? `Proposta: ${propose.data.chosenTicker} (confidenza ${propose.data.confidence})`
                  : 'Nessuna corrispondenza certa'
              }
            >
              <p>{propose.data.reason}</p>
              {propose.data.chosenTicker && !propose.data.nameMatch && (
                <p>
                  <strong>Attenzione:</strong> il nome della riga scelta
                  {propose.data.match ? ` («${propose.data.match.name}»)` : ''} non ha relazione con
                  «{instrumentName}». Controlla che sia davvero la stessa società prima di confermare.
                </p>
              )}
              {propose.data.runnerUpTicker && <p>Seconda scelta: {propose.data.runnerUpTicker}.</p>}
              {propose.data.chosenTicker && (
                <Button type="button" onClick={() => confirm(propose.data!.chosenTicker)} disabled={assign.isPending}>
                  Usa {propose.data.chosenTicker}
                </Button>
              )}
            </Alert>
          )}

          {search.error && (
            <Alert variant="error" title="Ricerca non riuscita">
              {errorMessage(search.error, 'Errore imprevisto')}
            </Alert>
          )}

          {search.isSuccess && results.length === 0 && (
            <Alert variant="warning" title="Nessun risultato">
              Il catalogo del broker non contiene nulla che corrisponda a «{query.trim()}». Prova con il
              nome esteso, o con il ticker così come appare nell'app del broker.
            </Alert>
          )}

          {results.length > 0 && (
            <ul className="broker-ticker__results">
              {results.map((match) => (
                <li key={match.ticker}>
                  <label className="broker-ticker__result">
                    <input
                      type="radio"
                      name={`ticker-${instrumentId}`}
                      value={match.ticker}
                      checked={selected === match.ticker}
                      onChange={() => setSelected(match.ticker)}
                    />
                    <span className="broker-ticker__result-body">
                      <span className="broker-ticker__result-head">
                        <strong>{match.ticker}</strong>
                        {match.isinMatch && <Badge tone="positive">ISIN coincidente</Badge>}
                      </span>
                      <span className="broker-ticker__result-meta">{describe(match)}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {assign.error && (
            <Alert variant="error" title="Associazione non riuscita">
              {errorMessage(assign.error, 'Errore imprevisto')}
            </Alert>
          )}

          <div className="broker-ticker__actions">
            {saved && (
              <Button type="button" variant="ghost" onClick={() => confirm(null)} disabled={assign.isPending}>
                Rimuovi associazione
              </Button>
            )}
            <Button
              type="button"
              onClick={() => selected && confirm(selected)}
              disabled={selected === null || assign.isPending}
            >
              {assign.isPending ? 'Associo…' : 'Associa il ticker scelto'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** Riga di dettaglio: serve a distinguere due ticker simili, quindi niente campi vuoti. */
function describe(match: TickerMatch): string {
  return [match.name, match.isin, match.currency, match.type].filter(Boolean).join(' · ');
}
